import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { getImageUrl } from '../../utils/imageUrl';
import { authAPI } from '../../services/api';
import Swal from 'sweetalert2';
import './Customer.css';

const Profile: React.FC = () => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const { profile, updateProfile } = useAuth();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [activeTab, setActiveTab] = useState<'personal' | 'address' | 'security'>('personal');

  // Personal & Address Form
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
  });

  // Avatar Uploading
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Password Form
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    new_password2: '',
  });

  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        phone: profile.phone || '',
        address: profile.address || '',
        city: profile.city || '',
        postal_code: profile.postal_code || '',
      });

      if (profile.profile_picture) {
        setAvatarPreview(getImageUrl(profile.profile_picture));
      }
    }
  }, [profile]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handlePasswordChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // Trigger hidden file picker
  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  // Handle image file selection & live preview
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      Swal.fire({
        icon: 'error',
        title: currentLang === 'bn' ? 'ভুল ফরম্যাট' : 'Invalid Format',
        text: currentLang === 'bn' ? 'অনুগ্রহ করে একটি ছবি ফাইল (.jpg, .png) নির্বাচন করুন।' : 'Please select an image file (.jpg, .png).',
        confirmButtonColor: '#059669',
      });
      return;
    }

    // Set preview
    setSelectedFile(file);
    const localUrl = URL.createObjectURL(file);
    setAvatarPreview(localUrl);

    // Auto Upload
    try {
      setUploadingAvatar(true);
      const data = new FormData();
      data.append('profile_picture', file);
      await updateProfile(data);

      Swal.fire({
        icon: 'success',
        title: currentLang === 'bn' ? 'ছবি আপডেট সফল!' : 'Avatar Updated!',
        text: currentLang === 'bn' ? 'আপনার প্রোফাইল ছবি সফলভাবে সংরক্ষণ করা হয়েছে।' : 'Your profile picture has been updated successfully.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } catch (err) {
      console.error('Failed to upload picture:', err);
      Swal.fire({
        icon: 'error',
        title: currentLang === 'bn' ? 'আপলোড ব্যর্থ' : 'Upload Failed',
        text: currentLang === 'bn' ? 'প্রোফাইল ছবি আপডেট করা সম্ভব হয়নি।' : 'Failed to update profile picture.',
        confirmButtonColor: '#ef4444',
      });
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Save profile info form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await updateProfile(formData);
      Swal.fire({
        icon: 'success',
        title: currentLang === 'bn' ? 'প্রোফাইল আপডেট হয়েছে!' : 'Profile Updated!',
        text: currentLang === 'bn' ? 'আপনার প্রোফাইল তথ্য সফলভাবে সংরক্ষিত হয়েছে।' : 'Your profile information has been saved successfully.',
        confirmButtonColor: '#059669',
        timer: 2500,
      });
    } catch (err: any) {
      console.error('Profile update error:', err);
      const serverErr = err?.response?.data;
      let errMsg = currentLang === 'bn' ? 'প্রোফাইল তথ্য আপডেট করতে সমস্যা হয়েছে।' : 'Failed to update profile details.';
      if (serverErr && typeof serverErr === 'object') {
        const firstKey = Object.keys(serverErr)[0];
        if (firstKey && Array.isArray(serverErr[firstKey])) {
          errMsg = `${firstKey}: ${serverErr[firstKey][0]}`;
        }
      }
      Swal.fire({
        icon: 'error',
        title: currentLang === 'bn' ? 'ত্রুটি' : 'Error',
        text: errMsg,
        confirmButtonColor: '#ef4444',
      });
    } finally {
      setLoading(false);
    }
  };

  // Change Password form handler
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordData.new_password !== passwordData.new_password2) {
      Swal.fire({
        icon: 'warning',
        title: currentLang === 'bn' ? 'পাসওয়ার্ড মেলেনি' : 'Passwords Do Not Match',
        text: currentLang === 'bn' ? 'নতুন পাসওয়ার্ড এবং নিশ্চিত পাসওয়ার্ড একই হতে হবে।' : 'New password and confirmation password must match.',
        confirmButtonColor: '#059669',
      });
      return;
    }

    if (passwordData.new_password.length < 6) {
      Swal.fire({
        icon: 'warning',
        title: currentLang === 'bn' ? 'দুর্বল পাসওয়ার্ড' : 'Weak Password',
        text: currentLang === 'bn' ? 'পাসওয়ার্ড কমপক্ষে ৬ অক্ষরের হতে হবে।' : 'Password must be at least 6 characters long.',
        confirmButtonColor: '#059669',
      });
      return;
    }

    setPasswordLoading(true);
    try {
      await authAPI.changePassword(
        passwordData.old_password,
        passwordData.new_password,
        passwordData.new_password2
      );

      setPasswordData({ old_password: '', new_password: '', new_password2: '' });

      Swal.fire({
        icon: 'success',
        title: currentLang === 'bn' ? 'পাসওয়ার্ড পরিবর্তিত!' : 'Password Changed!',
        text: currentLang === 'bn' ? 'আপনার পাসওয়ার্ড সফলভাবে আপডেট করা হয়েছে।' : 'Your password has been updated successfully.',
        confirmButtonColor: '#059669',
      });
    } catch (err: any) {
      console.error('Password change error:', err);
      const errMsg = err?.response?.data?.error || err?.response?.data?.old_password?.[0] || (currentLang === 'bn' ? 'বর্তমান পাসওয়ার্ড সঠিক নয়।' : 'Failed to change password.');
      Swal.fire({
        icon: 'error',
        title: currentLang === 'bn' ? 'ত্রুটি' : 'Error',
        text: errMsg,
        confirmButtonColor: '#ef4444',
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  if (!profile) {
    return (
      <div className="customer-container">
        <div className="profile-skeleton">
          <div className="skeleton-hero" />
          <div className="skeleton-card" />
        </div>
      </div>
    );
  }

  const fullName = profile.first_name || profile.last_name
    ? `${profile.first_name} ${profile.last_name}`.trim()
    : profile.username;

  return (
    <div className="customer-container profile-redesign-wrapper">
      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Hero Header Card */}
      <div className="profile-hero-card">
        <div className="hero-banner-pattern" />
        <div className="hero-content">
          {/* Avatar Upload Container */}
          <div className="profile-avatar-container" onClick={handleAvatarClick} title={currentLang === 'bn' ? 'ছবি পরিবর্তন করুন' : 'Change Profile Picture'}>
            <div className="avatar-ring">
              {avatarPreview ? (
                <img src={avatarPreview} alt={fullName} className="profile-avatar-img" />
              ) : (
                <div className="avatar-placeholder">
                  {fullName.charAt(0).toUpperCase()}
                </div>
              )}

              {/* Camera Hover Badge */}
              <div className="avatar-upload-overlay">
                {uploadingAvatar ? (
                  <span className="avatar-spinner" />
                ) : (
                  <svg className="camera-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                )}
              </div>
            </div>
            <button type="button" className="change-photo-badge">
              <svg className="badge-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
              {currentLang === 'bn' ? 'ছবি পরিবর্তন' : 'Change Photo'}
            </button>
          </div>

          {/* User Details Header */}
          <div className="profile-user-details">
            <div className="user-title-row">
              <h1 className="profile-user-name">{fullName}</h1>
              <span className="verified-status-tag">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
                {currentLang === 'bn' ? 'ভেরিফায়েড কাস্টমার' : 'Verified Customer'}
              </span>
            </div>

            <div className="user-meta-badges">
              <span className="meta-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                {profile.email}
              </span>
              <span className="meta-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                @{profile.username}
              </span>
              {profile.phone && (
                <span className="meta-badge">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                  {profile.phone}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Dashboard */}
      <div className="profile-dashboard-layout">
        {/* Navigation Tabs Header */}
        <div className="profile-tabs-nav">
          <button
            type="button"
            className={`tab-nav-btn ${activeTab === 'personal' ? 'active' : ''}`}
            onClick={() => setActiveTab('personal')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            {currentLang === 'bn' ? 'ব্যক্তিগত তথ্য' : 'Personal Info'}
          </button>

          <button
            type="button"
            className={`tab-nav-btn ${activeTab === 'address' ? 'active' : ''}`}
            onClick={() => setActiveTab('address')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            {currentLang === 'bn' ? 'ডেলিভারি ঠিকানা' : 'Shipping Address'}
          </button>

          <button
            type="button"
            className={`tab-nav-btn ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            {currentLang === 'bn' ? 'নিরাপত্তা ও পাসওয়ার্ড' : 'Security & Password'}
          </button>
        </div>

        {/* Tab 1: Personal Info */}
        {activeTab === 'personal' && (
          <div className="profile-tab-card">
            <div className="tab-card-header">
              <h3>{currentLang === 'bn' ? 'ব্যক্তিগত তথ্য সম্পাদনা' : 'Personal Information'}</h3>
              <p>{currentLang === 'bn' ? 'আপনার নাম ও যোগাযোগের প্রাথমিক তথ্য আপডেট রাখুন' : 'Keep your name and contact details up to date'}</p>
            </div>

            <form onSubmit={handleSubmit} className="profile-smart-form">
              <div className="form-grid-2">
                <div className="smart-form-group">
                  <label htmlFor="first_name">{currentLang === 'bn' ? 'প্রথম নাম' : 'First Name'} *</label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    placeholder={currentLang === 'bn' ? 'উদা: মোঃ' : 'e.g. John'}
                    disabled={loading}
                  />
                </div>

                <div className="smart-form-group">
                  <label htmlFor="last_name">{currentLang === 'bn' ? 'শেষ নাম' : 'Last Name'} *</label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    placeholder={currentLang === 'bn' ? 'উদা: করিম' : 'e.g. Doe'}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-grid-2">
                <div className="smart-form-group">
                  <label htmlFor="phone">{currentLang === 'bn' ? 'ফোন নম্বর' : 'Phone Number'} *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="01XXXXXXXXX"
                    disabled={loading}
                  />
                </div>

                <div className="smart-form-group">
                  <label>{currentLang === 'bn' ? 'ইউজারনেম (পরিবর্তনযোগ্য নয়)' : 'Username (Read Only)'}</label>
                  <input
                    type="text"
                    value={profile.username}
                    disabled
                    className="input-disabled"
                  />
                </div>
              </div>

              <div className="smart-form-group">
                <label>{currentLang === 'bn' ? 'ইমেইল অ্যাড্রেস (পরিবর্তনযোগ্য নয়)' : 'Email Address (Read Only)'}</label>
                <input
                  type="email"
                  value={profile.email}
                  disabled
                  className="input-disabled"
                />
              </div>

              <div className="form-action-row">
                <button type="submit" className="save-primary-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="btn-spinner" />
                      {currentLang === 'bn' ? 'সংরক্ষণ হচ্ছে...' : 'Saving...'}
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                        <polyline points="17 21 17 13 7 13 7 21" />
                        <polyline points="7 3 7 8 15 8" />
                      </svg>
                      {currentLang === 'bn' ? 'তথ্য পরিবর্তন সংরক্ষণ করুন' : 'Save Changes'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Tab 2: Shipping Address */}
        {activeTab === 'address' && (
          <div className="profile-tab-card">
            <div className="tab-card-header">
              <h3>{currentLang === 'bn' ? 'ডেলিভারি ঠিকানা' : 'Shipping Address'}</h3>
              <p>{currentLang === 'bn' ? 'পণ্য দ্রুত ডেলিভারির জন্য আপনার স্থায়ী ঠিকানা প্রদান করুন' : 'Ensure accurate and fast delivery of your orders'}</p>
            </div>

            <form onSubmit={handleSubmit} className="profile-smart-form">
              <div className="smart-form-group">
                <label htmlFor="address">{currentLang === 'bn' ? 'পূর্ণাঙ্গ ঠিকানা' : 'Street Address'} *</label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  rows={3}
                  placeholder={currentLang === 'bn' ? 'বাসা/ফ্ল্যাট নম্বর, রোড, এলাকা' : 'House/Flat No, Road, Area'}
                  disabled={loading}
                />
              </div>

              <div className="form-grid-2">
                <div className="smart-form-group">
                  <label htmlFor="city">{currentLang === 'bn' ? 'শহর / জেলা' : 'City / District'} *</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    placeholder={currentLang === 'bn' ? 'উদা: ঢাকা' : 'e.g. Dhaka'}
                    disabled={loading}
                  />
                </div>

                <div className="smart-form-group">
                  <label htmlFor="postal_code">{currentLang === 'bn' ? 'পোস্টাল কোড' : 'Postal Code'} *</label>
                  <input
                    type="text"
                    id="postal_code"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleChange}
                    required
                    placeholder="1207"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-action-row">
                <button type="submit" className="save-primary-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="btn-spinner" />
                      {currentLang === 'bn' ? 'সংরক্ষণ হচ্ছে...' : 'Saving...'}
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                        <polyline points="17 21 17 13 7 13 7 21" />
                        <polyline points="7 3 7 8 15 8" />
                      </svg>
                      {currentLang === 'bn' ? 'ঠিকানা আপডেট করুন' : 'Update Address'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Tab 3: Security & Password */}
        {activeTab === 'security' && (
          <div className="profile-tab-card">
            <div className="tab-card-header">
              <h3>{currentLang === 'bn' ? 'পাসওয়ার্ড পরিবর্তন' : 'Change Password'}</h3>
              <p>{currentLang === 'bn' ? 'অ্যাকাউন্ট সুরক্ষিত রাখতে নিয়মিত শক্তিশালী পাসওয়ার্ড ব্যবহার করুন' : 'Keep your account secure with a strong password'}</p>
            </div>

            <form onSubmit={handlePasswordSubmit} className="profile-smart-form">
              <div className="smart-form-group">
                <label htmlFor="old_password">{currentLang === 'bn' ? 'বর্তমান পাসওয়ার্ড' : 'Current Password'} *</label>
                <input
                  type="password"
                  id="old_password"
                  name="old_password"
                  value={passwordData.old_password}
                  onChange={handlePasswordChangeInput}
                  required
                  placeholder="••••••••"
                  disabled={passwordLoading}
                />
              </div>

              <div className="form-grid-2">
                <div className="smart-form-group">
                  <label htmlFor="new_password">{currentLang === 'bn' ? 'নতুন পাসওয়ার্ড' : 'New Password'} *</label>
                  <input
                    type="password"
                    id="new_password"
                    name="new_password"
                    value={passwordData.new_password}
                    onChange={handlePasswordChangeInput}
                    required
                    placeholder="••••••••"
                    disabled={passwordLoading}
                  />
                </div>

                <div className="smart-form-group">
                  <label htmlFor="new_password2">{currentLang === 'bn' ? 'নতুন পাসওয়ার্ড নিশ্চিত করুন' : 'Confirm New Password'} *</label>
                  <input
                    type="password"
                    id="new_password2"
                    name="new_password2"
                    value={passwordData.new_password2}
                    onChange={handlePasswordChangeInput}
                    required
                    placeholder="••••••••"
                    disabled={passwordLoading}
                  />
                </div>
              </div>

              <div className="form-action-row">
                <button type="submit" className="save-primary-btn security-btn" disabled={passwordLoading}>
                  {passwordLoading ? (
                    <>
                      <span className="btn-spinner" />
                      {currentLang === 'bn' ? 'পাসওয়ার্ড আপডেট হচ্ছে...' : 'Updating Password...'}
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                      {currentLang === 'bn' ? 'পাসওয়ার্ড আপডেট করুন' : 'Update Password'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
